import React, { useContext, useMemo, useState } from 'react';

import FAQ from 'src/areas/editorial-content/components/slices/faq';
import { AdvizedCarousel } from 'src/components/advized';
import { AppContext } from 'src/components/app-context';
import { PageContent } from 'src/components/page';
import { useViewportContext } from 'src/components/viewport/context';
import WordCloud from 'src/components/word-cloud';

import {
  Breadcrumb,
  DealsContextWrapper,
  Description,
  FeaturedArticles,
  Filter,
  HeaderBlock,
  Navigation,
  Products,
  SecondaryNavigation,
} from '..';

import type {
  AggregationSection,
  ProductCollection as ProductCollectionType,
  ProductCollectionPageQuery,
} from 'src/types';

import RouteContext from 'src/routes/context';
import { FilterContext } from '../../filter-context';
import {
  ProductCollectionFilterContext,
  ProductCollectionFilterTrackingScopeContext,
  ProductCollectionTrackingContext,
} from '../../filter-tracking';

import { discriminatedArrayToMap } from 'src/utils';
import { SiteWidthWrapper } from '../../styles';
import { formatBreadcrumbs } from '../../utils/format-breadcrumbs';
import { aggregationReceive } from '../../utils/product-collections';
import { FilterScope } from 'src/globals/tracking/filter';

const trackingScopeContextValue = {
  scope: FilterScope.CATEGORY,
};

const productCollectionFilterContextValue = {
  filterLayout: 'facet-navigation' as const,
};

export const ProductCollection = ({
  data,
  aggData,
}: {
  data: ProductCollectionPageQuery;
  aggData: AggregationSection[];
}) => {
  const { currentRouteState } = useContext(RouteContext);
  // route state
  const isPaginated = currentRouteState.offset > 0;

  const productCollection = aggData.length
    ? aggregationReceive(
        data?.productCollection as ProductCollectionType,
        aggData
      )
    : (data?.productCollection as ProductCollectionType);

  const { popularSearchTerms, slices, slug, name } = productCollection || {};

  const { t, linkTo } = useContext(AppContext);
  const { isMobile, isTablet } = useViewportContext();
  const removeHeaderMargins = isMobile || isTablet;

  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [count, setCount] = useState(0);

  const {
    BreadcrumbsSlice,
    HeaderBlockSlice,
    FilterSlice,
    NavigationSlice,
    ProductsSlice,
    DescriptionSlice,
    PrismicSliceFaqSection,
    FeaturedArticlesSlice,
    ExpertSlice,
    SecondaryNavigationSlice,
  } = discriminatedArrayToMap(slices, '__typename');

  const trackingContextValue = useMemo(
    () => ({ categoryId: slug, categoryName: name }),
    [slug, name]
  );

  const filterContextValue = useMemo(
    () => ({ mobileFilterOpen, setMobileFilterOpen, count, setCount }),
    [count, mobileFilterOpen]
  );

  return (
    <ProductCollectionFilterTrackingScopeContext.Provider
      value={trackingScopeContextValue}
    >
      <ProductCollectionTrackingContext.Provider value={trackingContextValue}>
        <FilterContext.Provider value={filterContextValue}>
          <PageContent data-test="ProductCollectionPage" withAds>
            {BreadcrumbsSlice ? (
              <Breadcrumb
                id={BreadcrumbsSlice.id}
                items={formatBreadcrumbs(
                  BreadcrumbsSlice.items,
                  productCollection.categoryPath
                )}
                categoryIds={productCollection.categories?.map(
                  category => category.id
                )}
              />
            ) : null}
            {HeaderBlockSlice ? (
              <HeaderBlock
                title={HeaderBlockSlice.title}
                pagination={HeaderBlockSlice.pagination}
                noMargins={removeHeaderMargins}
              />
            ) : null}
            {FilterSlice ? (
              <Filter
                id={FilterSlice.id}
                sections={FilterSlice.sections}
                filters={FilterSlice.filters}
                productsCount={FilterSlice.productsCount}
              />
            ) : null}
            {NavigationSlice ? (
              <ProductCollectionFilterContext.Provider
                value={productCollectionFilterContextValue}
              >
                <Navigation
                  id={NavigationSlice.id}
                  links={NavigationSlice.links}
                  property={NavigationSlice.property}
                  key={`${NavigationSlice.id}_${
                    Array.isArray(NavigationSlice.links)
                      ? NavigationSlice.links.map(({ id }) => id).join(',')
                      : ''
                  }`}
                />
              </ProductCollectionFilterContext.Provider>
            ) : null}
            {ProductsSlice ? (
              <DealsContextWrapper dealsItems={ProductsSlice.dealsItems}>
                <Products
                  id={ProductsSlice.id}
                  pageInfo={ProductsSlice.pageInfo}
                  products={ProductsSlice.products}
                  productsCount={ProductsSlice.productsCount}
                  storeCount={ProductsSlice.storeCount}
                  legalText={ProductsSlice.legalText}
                  availableSorts={ProductsSlice.availableSorts}
                  currentSort={ProductsSlice.currentSort}
                  maxHitsAmount={ProductsSlice.maxHitsAmount}
                  sortDirection={ProductsSlice.sortDirection}
                  propertyColumns={ProductsSlice.propertyColumns}
                  expert={ExpertSlice?.expert}
                  productCollection={productCollection}
                  preferredViewMode={ProductsSlice.preferredViewMode}
                />
              </DealsContextWrapper>
            ) : null}
            <SiteWidthWrapper>
              {DescriptionSlice ? (
                <Description
                  productCollection={productCollection}
                  description={DescriptionSlice.description}
                />
              ) : null}
              {PrismicSliceFaqSection ? (
                <FAQ
                  faqTitle={PrismicSliceFaqSection.faqTitle}
                  items={PrismicSliceFaqSection.items}
                />
              ) : null}
              {FeaturedArticlesSlice ? (
                <FeaturedArticles
                  featuredArticlesSlice={FeaturedArticlesSlice}
                />
              ) : null}
              {!isPaginated && ExpertSlice ? (
                <AdvizedCarousel
                  offset={ExpertSlice.offset}
                  isList={false}
                  numberOfCards={isTablet ? 5 : 3}
                  hideExperts={false}
                  {...ExpertSlice.expert}
                />
              ) : null}
              {SecondaryNavigationSlice ? (
                <SecondaryNavigation
                  id={SecondaryNavigationSlice.id}
                  key={SecondaryNavigationSlice.id}
                  productCollection={productCollection}
                  navigationItems={SecondaryNavigationSlice.navigationItems}
                />
              ) : null}
              {Array.isArray(popularSearchTerms) &&
              popularSearchTerms.length ? (
                <WordCloud
                  title={t('search', 'search.related-searches')}
                  items={popularSearchTerms.slice(0, 100)}
                  showMoreUrl={linkTo('keywordListing', productCollection)}
                  numberOfVisibleItems={35}
                />
              ) : null}
            </SiteWidthWrapper>
          </PageContent>
        </FilterContext.Provider>
      </ProductCollectionTrackingContext.Provider>
    </ProductCollectionFilterTrackingScopeContext.Provider>
  );
};
